import React, { useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';

import routeMap from './services/Routes';

import AuthLayout from './elements/AuthLayout';
import DefaultLayout from './elements/DefaultLayout';
import { UserContext } from './contexts/UserContext';

import {
  IGetUserIdentityQuery,
  IGetUserIdentityQueryVariables,
} from './graphql/queries/getUserIdentity/queries.types';
import { GET_USER_IDENTITY } from './graphql/queries/getUserIdentity/queries';
import useNotifications from './hooks/useNotifications';
import Notifications from './elements/Notifications';
import { GlobalNotificationContext } from './contexts/GlobalNotificationContext';
import useLogging from './hooks/useLogging';

// Lazy loaded scenes
const Login = React.lazy(() => import('./scenes/Login'));
const ResetPassword = React.lazy(() => import('./scenes/ResetPassword'));
const Home = React.lazy(() => import('./scenes/Home'));
const Customer = React.lazy(() => import('./scenes/Customer'));
const Order = React.lazy(() => import('./scenes/Order'));
const Search = React.lazy(() => import('./scenes/Search'));
const Suborder = React.lazy(() => import('./scenes/Suborder'));

const App = () => {
  const { data, loading, error, refetch } = useQuery<
    IGetUserIdentityQuery,
    IGetUserIdentityQueryVariables
  >(GET_USER_IDENTITY);

  const globalNotifications = useNotifications();
  const sendLog = useLogging();

  const userContextValue = useMemo(() => {
    const { identity } = data ?? {};
    const { currentConcierge, currentAdmin } = identity ?? {};
    return {
      loading,
      error,
      currentAdmin,
      currentConcierge,
      isLoggedIn:
        !!currentConcierge &&
        !loading &&
        !error &&
        (Number(currentConcierge?.id ?? 0) > 0 || Number(currentAdmin?.id ?? 0) > 0),
      refetchIdentity: refetch,
    };
  }, [data, refetch, loading, error]);

  if (error) {
    sendLog('LitX lost connection with the monolith', { userContextValue }, 'critical');
    return (
      <Notifications errors={[`${error.message}. Please contact tech support for assistance.`]} />
    );
  }
  if (!loading && !data) {
    sendLog('LitX lost connection with the monolith', { userContextValue }, 'critical');
    return (
      <Notifications
        errors={[
          'An error occurred where data could not be found. Please contact tech support for assistance.',
        ]}
      />
    );
  }

  return (
    <React.Suspense fallback={<p>Loading...</p>}>
      <GlobalNotificationContext.Provider value={globalNotifications}>
        <UserContext.Provider value={userContextValue}>
          <Routes>
            {/* No login required */}
            <Route element={<AuthLayout />}>
              <Route path={routeMap.login()} element={<Login />} />
              <Route path={routeMap.resetPassword()} element={<ResetPassword />} />
            </Route>

            <Route path={routeMap.home()} element={<DefaultLayout />}>
              {/* Requires login */}
              <Route index path={routeMap.home()} element={<Home />} />
              <Route path={`${routeMap.customer()}/*`} element={<Customer />} />
              <Route path={`${routeMap.order()}/*`} element={<Order />} />
              <Route path={routeMap.search()} element={<Search />} />
              <Route path={`${routeMap.suborder()}/*`} element={<Suborder />} />
            </Route>
          </Routes>
        </UserContext.Provider>
      </GlobalNotificationContext.Provider>
    </React.Suspense>
  );
};

export default App;
